import { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Button, Form, Row, Spin, message as antMessage } from 'antd';
import {
  CloseOutlined,
  CheckOutlined,
  MailOutlined,
  PrinterOutlined
} from '@ant-design/icons';
import { useMediaQuery } from 'react-responsive';
import useAuthContext from '../../contexts/AuthContext';
import useErrorMessage from '../../utils/ErrorMessage';
import ContentCustom from '../ContentCustom/ContentCustom';
import PageHeader from '../PageHeader/PageHeader';
import useGenerateFormItem from '../../utils/GenerateFormItem';
import {
  formItemLayout,
  tailFormItemLayout
} from '../../utils/constants/formLayout';

const CreateUpdateContainer = ({
  purpose,
  fields,
  loadingFields,
  resource,
  baseUrl,
  config,
  formExtra,
  tradKey,
  submitLabel,
  customSubmit,
  isParentLoading,
  customForm
}) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { t } = useTranslation();
  const { message } = useErrorMessage();
  const { dispatchAPI, user } = useAuthContext();
  const [isLoading, setIsLoading] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const generateFields = useGenerateFormItem();
  const { onGetResource, onCreateResource, onUpdateResource } = config;

  const isReductScreen = useMediaQuery({ query: '(max-width: 1400px)' });

  let form = Form.useForm()[0];
  if (customForm) {
    form = customForm;
  }

  const printCompliance = async () => {
    setIsDownloading(true);
    try {
      const { data } = await dispatchAPI('GET', {
        url: `/insurers/compliance/${user?.insurer?._id}`,
        responseType: 'blob'
      });
      const blob = new Blob([data], {
        contentType: 'application/pdf'
      });

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Conformité de ${user?.insurer?.name}.pdf`;
      a.click();
      antMessage.success(t('export.messages.success'));
    } catch (e) {
      if (e.response) message(e.response.status);
    }
    setIsDownloading(false);
  };

  const updateResource = async (body) => {
    setIsLoading(true);
    try {
      await dispatchAPI('PATCH', {
        url: `${baseUrl}/${
          purpose === 'edit_compliance' ? user?.insurer?._id : id
        }`,
        body:
          onUpdateResource && onUpdateResource.setBody
            ? onUpdateResource.setBody(body)
            : body
      });
      navigate(-1);
    } catch (e) {
      if (e.response) message(e.response.status);
    }
    setIsLoading(false);
  };

  const createResource = async (body) => {
    setIsLoading(true);
    try {
      await dispatchAPI('POST', {
        url: `${baseUrl}`,
        body:
          onCreateResource && onCreateResource.setBody
            ? onCreateResource.setBody(body)
            : body
      });
      navigate(-1);
    } catch (e) {
      if (e.response) message(e.response.status);
    }
    setIsLoading(false);
  };

  const getResource = useCallback(async () => {
    setIsLoading(true);
    try {
      const { data } = await dispatchAPI('GET', {
        url: `${baseUrl}/${
          purpose === 'edit_compliance' ? user?.insurer?._id : id
        }`
      });
      form.setFieldsValue(
        onGetResource && onGetResource.setFields
          ? onGetResource.setFields(data)
          : data
      );
    } catch (e) {
      if (e.response) message(e.response.status);
    }
    setIsLoading(false);
    // eslint-disable-next-line
  }, [purpose, id, loadingFields]);

  useEffect(() => {
    if ((purpose === 'edit' && id) || (purpose === 'edit_compliance' && id)) {
      setIsLoading(true);
      if (!loadingFields)
        (async () => {
          await getResource();
        })();
    }
    // eslint-disable-next-line
  }, [getResource]);

  // eslint-disable-next-line consistent-return
  const handleSubmit = async (values) => {
    if (customSubmit) return customSubmit(values);
    if (purpose === 'edit') await updateResource(values);
    if (purpose === 'create') await createResource(values);
    if (purpose === 'edit_compliance') await updateResource(values);
  };

  return (
    <>
      <PageHeader
        title={t(`${resource}.form.title.${purpose}`)}
        extra={
          (resource === 'insurers' && purpose === 'edit') ||
          (purpose === 'edit_compliance' && (
            <>
              <Button
                type="primary"
                style={{
                  width: isReductScreen ? 35 : 'auto',
                  padding: isReductScreen ? '4px 4px' : '4px 15px'
                }}
              >
                <a
                  target="_blank"
                  rel="noopener noreferrer"
                  href={`mailto:${t('contacts.menuContact.info.mail')}`}
                >
                  <span style={{ display: isReductScreen ? 'none' : 'inline' }}>
                    {t('breadcrumbs.contacts')}
                  </span>
                  <MailOutlined
                    style={{
                      marginLeft: isReductScreen ? 0 : 8
                    }}
                  />
                </a>
              </Button>
              {purpose === 'edit_compliance' && (
                <Button
                  onClick={printCompliance}
                  loading={isDownloading}
                  type="primary"
                  style={{
                    width: isReductScreen ? 35 : 'auto',
                    padding: isReductScreen ? '4px 4px' : '4px 15px'
                  }}
                >
                  <span style={{ display: isReductScreen ? 'none' : 'inline' }}>
                    {t('buttons.print_compliance')}
                  </span>
                  <PrinterOutlined
                    style={{
                      marginLeft: isReductScreen ? 0 : 8
                    }}
                  />
                </Button>
              )}
            </>
          ))
        }
      />
      <ContentCustom>
        <Spin spinning={isLoading || !!isParentLoading}>
          <Form
            /* eslint-disable-next-line react/jsx-props-no-spreading */
            {...formItemLayout}
            onFinish={handleSubmit}
            form={form}
          >
            {fields.map((field) => generateFields(tradKey || resource, field))}
            {formExtra}
            {/* eslint-disable-next-line react/jsx-props-no-spreading */}
            <Form.Item {...tailFormItemLayout}>
              <Row justify="end">
                <Button
                  style={{ margin: '0 10px' }}
                  type="link"
                  danger
                  onClick={() => navigate(-1)}
                >
                  {`${t('buttons.cancel')} `}
                  <CloseOutlined />
                </Button>
                <Button type="add" htmlType="submit">
                  <CheckOutlined />
                  {`${t(submitLabel || 'buttons.save')} `}
                </Button>
              </Row>
            </Form.Item>
          </Form>
        </Spin>
      </ContentCustom>
    </>
  );
};

CreateUpdateContainer.propTypes = {
  purpose: PropTypes.string.isRequired,
  fields: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  baseUrl: PropTypes.string.isRequired,
  resource: PropTypes.string.isRequired,
  loadingFields: PropTypes.bool,
  config: PropTypes.shape({
    onGetResource: PropTypes.shape({
      setFields: PropTypes.func
    }),
    onCreateResource: PropTypes.shape({
      setBody: PropTypes.func
    }),
    onUpdateResource: PropTypes.shape({
      setBody: PropTypes.func
    })
  }),
  formExtra: PropTypes.element,
  tradKey: PropTypes.string,
  submitLabel: PropTypes.string,
  customSubmit: PropTypes.func,
  isParentLoading: PropTypes.bool,
  customForm: PropTypes.func,
  canScan: PropTypes.bool
};

CreateUpdateContainer.defaultProps = {
  config: {},
  loadingFields: false,
  formExtra: null,
  tradKey: null,
  submitLabel: null,
  customSubmit: null,
  isParentLoading: false,
  customForm: null,
  canScan: false
};

export default CreateUpdateContainer;
